import type { CurrentSubscription, SubscriptionUsage } from "@/types";
import { formatDate } from "@/utils/formatters";

type UsageStatsProps = {
  subscription: CurrentSubscription;
  usage: SubscriptionUsage;
};

export default function UsageStats({ subscription, usage }: UsageStatsProps) {
  const auditProgress = Math.min(Math.max((usage.audits_used / Math.max(usage.audit_limit, 1)) * 100, 0), 100);
  const refreshLabel = usage.audits_refresh_at ? formatDate(usage.audits_refresh_at) : null;
  const domainLimitLabel = usage.domain_limit == null ? "Unlimited" : String(usage.domain_limit);

  return (
    <section className="card">
      <div className="card-body">
        <h2 className="mb-4 text-lg font-semibold">Usage Statistics</h2>

        <div className="mb-4 border-b border-gray-200 pb-4">
          <div className="mb-2 flex justify-between">
            <span className="text-gray-600">Page audits</span>
            <span className="font-semibold">
              {usage.audits_remaining} remaining
              <span className="ml-2 font-normal text-gray-500">
                ({usage.audits_used} / {usage.audit_limit} used)
              </span>
            </span>
          </div>

          <div className="h-2.5 w-full rounded-full bg-gray-200">
            <div className="h-2.5 rounded-full bg-primary" style={{ width: `${auditProgress}%` }} />
          </div>

          <p className="mt-2 text-sm text-gray-500">
            {refreshLabel
              ? `Quota refreshes on ${refreshLabel}.`
              : "Free plan page audits are a one-time lifetime budget."}
          </p>
        </div>

        <div className="mb-4 border-b border-gray-200 pb-4">
          <div className="flex justify-between">
            <span className="text-gray-600">Domains</span>
            <span className="font-semibold">
              {usage.domains_used} / {domainLimitLabel}
            </span>
          </div>
        </div>

        <div>
          <h3 className="mb-3 text-base font-semibold">
            {planName(usage.plan)} Plan
            {subscription?.manual_grant ? " - Free gift" : ""}
          </h3>
          <ul className="space-y-2">
            <li>
              {usage.plan === "free"
                ? `${usage.audit_limit} lifetime page audits`
                : `${usage.audit_limit} page audits per billing period`}
            </li>
            <li>
              {usage.domain_limit == null
                ? "Unlimited domains"
                : `Up to ${usage.domain_limit} ${usage.domain_limit === 1 ? "domain" : "domains"}`}
            </li>
            <li>
              Need more capacity?{" "}
              <a href="/contact" className="text-primary-600 hover:underline">
                Contact us
              </a>
            </li>
          </ul>
        </div>
      </div>
    </section>
  );
}

function planName(plan: string) {
  return plan.charAt(0).toUpperCase() + plan.slice(1);
}
